import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { apiDelete, apiGet, apiPost, apiPut } from '../services/apiClient';

const statusVariant = {
  mapped: 'success',
  unmapped: 'secondary',
  sync_error: 'danger',
};

const MappingModal = ({ show, onHide, onSave, initial, properties, connections }) => {
  const [form, setForm] = useState(() => ({
    property_id: initial?.property_id || '',
    provider_connection_id: initial?.provider_connection_id || '',
    external_property_id: initial?.external_property_id || '',
    external_listing_url: initial?.external_listing_url || '',
    sync_mode: initial?.sync_mode || 'two_way',
    sync_calendar: initial?.sync_calendar ?? true,
    sync_price: initial?.sync_price ?? true,
    sync_content: initial?.sync_content ?? false,
    sync_frequency_minutes: initial?.sync_frequency_minutes || 15,
    timezone: initial?.timezone || 'Asia/Kolkata',
    source_of_truth: initial?.source_of_truth || 'internal_wins',
  }));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      property_id: initial?.property_id || '',
      provider_connection_id: initial?.provider_connection_id || '',
      external_property_id: initial?.external_property_id || '',
      external_listing_url: initial?.external_listing_url || '',
      sync_mode: initial?.sync_mode || 'two_way',
      sync_calendar: initial?.sync_calendar ?? true,
      sync_price: initial?.sync_price ?? true,
      sync_content: initial?.sync_content ?? false,
      sync_frequency_minutes: initial?.sync_frequency_minutes || 15,
      timezone: initial?.timezone || 'Asia/Kolkata',
      source_of_truth: initial?.source_of_truth || 'internal_wins',
    });
    setError('');
  }, [initial, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.property_id || !form.provider_connection_id || !form.external_property_id) {
      setError('Property, connection, and external ID are required.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        sync_frequency_minutes: Number(form.sync_frequency_minutes || 15),
      });
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{initial ? 'Edit Mapping' : 'Create Mapping'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="row g-2">
            <Form.Group className="col-md-6">
              <Form.Label>Property</Form.Label>
              <Form.Select
                value={form.property_id}
                onChange={(e) => setForm((prev) => ({ ...prev, property_id: e.target.value }))}
                disabled={!!initial}
                required
              >
                <option value="">Select property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.title} ({property.property_code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>Connection</Form.Label>
              <Form.Select
                value={form.provider_connection_id}
                onChange={(e) => setForm((prev) => ({ ...prev, provider_connection_id: e.target.value }))}
                disabled={!!initial}
                required
              >
                <option value="">Select connection</option>
                {connections.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.label} ({conn.provider?.key})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="row g-2 mt-2">
            <Form.Group className="col-md-6">
              <Form.Label>External property ID</Form.Label>
              <Form.Control
                value={form.external_property_id}
                onChange={(e) => setForm((prev) => ({ ...prev, external_property_id: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>Listing URL (optional)</Form.Label>
              <Form.Control
                value={form.external_listing_url}
                onChange={(e) => setForm((prev) => ({ ...prev, external_listing_url: e.target.value }))}
              />
            </Form.Group>
          </div>
          <div className="row g-2 mt-2">
            <Form.Group className="col-md-4">
              <Form.Label>Sync mode</Form.Label>
              <Form.Select
                value={form.sync_mode}
                onChange={(e) => setForm((prev) => ({ ...prev, sync_mode: e.target.value }))}
              >
                <option value="two_way">two_way</option>
                <option value="pull">pull</option>
                <option value="push">push</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Frequency (min)</Form.Label>
              <Form.Control
                type="number"
                value={form.sync_frequency_minutes}
                onChange={(e) => setForm((prev) => ({ ...prev, sync_frequency_minutes: e.target.value }))}
                min={1}
              />
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Timezone</Form.Label>
              <Form.Control
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
              />
            </Form.Group>
          </div>
          <div className="row g-2 mt-2">
            <Form.Group className="col-md-4">
              <Form.Label>Source of truth</Form.Label>
              <Form.Select
                value={form.source_of_truth}
                onChange={(e) => setForm((prev) => ({ ...prev, source_of_truth: e.target.value }))}
              >
                <option value="internal_wins">internal_wins</option>
                <option value="external_wins">external_wins</option>
                <option value="manual_review">manual_review</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-8 d-flex align-items-center gap-3">
              <Form.Check
                type="switch"
                id="sync-calendar"
                label="Sync calendar"
                checked={form.sync_calendar}
                onChange={(e) => setForm((prev) => ({ ...prev, sync_calendar: e.target.checked }))}
              />
              <Form.Check
                type="switch"
                id="sync-price"
                label="Sync price"
                checked={form.sync_price}
                onChange={(e) => setForm((prev) => ({ ...prev, sync_price: e.target.checked }))}
              />
              <Form.Check
                type="switch"
                id="sync-content"
                label="Sync content"
                checked={form.sync_content}
                onChange={(e) => setForm((prev) => ({ ...prev, sync_content: e.target.checked }))}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const ChannelMappingsPage = ({ token }) => {
  const [properties, setProperties] = useState([]);
  const [connections, setConnections] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [propertiesRes, connectionsRes, mappingsRes] = await Promise.all([
        apiGet('/api/properties?pageSize=200', token),
        apiGet('/api/admin/channel/connections', token),
        apiGet('/api/admin/channel/mappings', token),
      ]);
      const props = propertiesRes.data || propertiesRes || [];
      setProperties(props);
      setConnections(connectionsRes || []);
      setMappings(mappingsRes || []);
    } catch (err) {
      setError(err.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (payload) => {
    if (editing) {
      await apiPut(`/api/admin/channel/mappings/${editing.id}`, payload, token);
    } else {
      await apiPost('/api/admin/channel/mappings', payload, token);
    }
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    await apiDelete(`/api/admin/channel/mappings/${id}`, token);
    await load();
  };

  const handleSync = async (id) => {
    await apiPost(`/api/admin/channel/mappings/${id}/sync`, {}, token);
    await load();
  };

  const mappingRows = useMemo(() => mappings, [mappings]);

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">API Management - Property Mappings</h4>
          <small className="text-muted">Map Homevilas properties to external listings.</small>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          + New Mapping
        </Button>
      </div>

      {loading && (
        <div className="py-4 text-center">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && (
        <Table bordered hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>Property</th>
              <th>Connection</th>
              <th>External ID</th>
              <th>Status</th>
              <th>Sync mode</th>
              <th>Frequency</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappingRows.map((mapping) => (
              <tr key={mapping.id}>
                <td>
                  <div className="fw-semibold">{mapping.property_title}</div>
                  <small className="text-muted">{mapping.property_slug}</small>
                </td>
                <td>
                  <div className="fw-semibold">{mapping.connection_label}</div>
                  <small className="text-muted">{mapping.provider_key}</small>
                </td>
                <td>{mapping.external_property_id}</td>
                <td>
                  <Badge bg={statusVariant[mapping.mapping_status] || 'secondary'}>
                    {mapping.mapping_status}
                  </Badge>
                </td>
                <td>{mapping.sync_mode}</td>
                <td>{mapping.sync_frequency_minutes} min</td>
                <td className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setEditing(mapping);
                      setModalOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="outline-success" onClick={() => handleSync(mapping.id)}>
                    Sync now
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(mapping.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
            {!mappingRows.length && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No mappings yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <MappingModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        onSave={handleSave}
        initial={editing}
        properties={properties}
        connections={connections}
      />
    </div>
  );
};

export default ChannelMappingsPage;
